import { Badge } from '@/components/ui'

import { batchLabel, useBatchPreview } from '@/hooks/features/register'
import { cn } from '@/lib/utils'
import { discountAmount, lineGross, lineNet } from '@/services'
import { PosModel } from '@/types'
import { formatMoney, formatQty } from '@/utils'

import { QtyStepper } from './QtyStepper'

/** Where the line's unit price came from (§7.1). */
export type PriceSource = 'price_book' | 'branch' | 'product' | 'entered'

const SOURCE_LABEL: Record<PriceSource, string> = {
  price_book: 'Price book',
  branch: 'Branch price',
  product: 'Product',
  entered: 'Entered',
}

interface CartLineProps {
  line: PosModel.CartLine
  /** The price charged for one unit for this customer (their price book's, else the line's). */
  unitPrice: number
  source: PriceSource
  /** Saving from an offer on this line (only lines without a manual discount get one). */
  offerOff?: number
  active: boolean
  onQty: (qty: number) => void
  onRemove: () => void
  onDiscount: () => void
  /** Sold by unit: read the scale (or type) the quantity again. */
  onWeigh?: () => void
}

export const CartLine = ({ line, unitPrice, source, offerOff = 0, active, onQty, onRemove, onDiscount, onWeigh }: CartLineProps) => {
  const { product, quantity, discount } = line
  const priced = { ...line, product: { ...product, price: unitPrice } }
  const off = discountAmount(lineGross(priced), discount)
  const batches = useBatchPreview(product._id, quantity)

  const chip = 'rounded-xs border border-border px-2 py-0.5 text-xs font-medium text-ink hover:bg-fill'

  return (
    <li className={cn('space-y-1.5 border-b border-border-subtle px-4 py-3 transition-colors', active && 'bg-subdued')}>
      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1 truncate text-sm font-semibold text-ink">{product.name}</p>
        <QtyStepper value={quantity} onChange={onQty} decimal={product.sold_by_unit} className="h-8 rounded-xs" />
        {product.sold_by_unit && <span className="text-xs text-muted-foreground">{product.unit}</span>}
        <span className="w-24 text-right text-sm font-semibold tabular-nums text-ink">{formatMoney(lineNet(priced) - offerOff)}</span>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 text-xs tabular-nums text-muted-foreground">
        <span>
          {formatMoney(unitPrice)} / {product.unit}
        </span>
        <Badge tone="info" className="rounded-xs">{SOURCE_LABEL[source]}</Badge>
        {product.taxable && !!product.sale_vat && <span>VAT {formatQty(product.sale_vat)}%</span>}
        {batches.length > 0 && (
          <Badge tone="soft" className="rounded-xs font-mono" title="Batches this sale takes stock from">
            batch {batches.map((b) => `${batchLabel(b.stock_purchase_transaction_id)} ×${formatQty(b.quantity)}`).join(' + ')}
          </Badge>
        )}
        {quantity > product.stock && <span className="text-warning-dark">past stock</span>}
        {off > 0 && <span className="text-success">−{formatMoney(off)}</span>}
        {offerOff > 0 && <span className="text-success">Offer −{formatMoney(offerOff)}</span>}
        <button type="button" onClick={onDiscount} className={cn(chip, discount && 'border-success bg-success-lighter text-success-dark')}>
          Discount
        </button>
        {onWeigh && (
          <button type="button" onClick={onWeigh} className={chip}>
            Weight
          </button>
        )}
        <button type="button" onClick={onRemove} aria-label={`Remove ${product.name}`} className={cn(chip, 'hover:bg-error-lighter hover:text-error')}>
          ×
        </button>
      </div>
    </li>
  )
}
